import { useMemo } from 'react';
import {
	PRODUCT_DISCOVERY_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
	CORE_PROJECT,
	type ProjectType,
	toProjectType,
} from '@jira/platform__project-types/src/project-types.tsx';
import {
	getEditionForProject,
	type ApplicationEdition,
} from '@jira/platform__application-editions/src/edition.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useSiteHasOnlyJira } from '@atlassian/jira-billing/src/controllers/explicitly-licensed-products/use-site-has-only-jira.tsx';
import type { Product, Edition } from '@atlassian/edition-awareness';

export const EDITION_MAP: Partial<Record<ApplicationEdition, Edition>> = {
	FREE_EDITION: 'free',
	STANDARD_EDITION: 'standard',
	PREMIUM_EDITION: 'premium',
};

export const PRODUCT_MAP: Partial<Record<ProjectType, Product>> = {
	[SOFTWARE_PROJECT]: 'jira',
	[CORE_PROJECT]: 'jira',
	[PRODUCT_DISCOVERY_PROJECT]: 'jira-product-discovery',
	[SERVICE_DESK_PROJECT]: 'jira-service-management',
};

type ProductAndEditionInfo = {
	product?: Product;
	edition?: Edition;
};

/**
 * Condenses some logic from ProductAndEditionChecker. This is to be used
 * during the integration of Jira with the AudienceAndTiming code in Platform.
 * This will eventually be cleaned up as more EA code is shifted to Platform
 * @returns { product, edition, buttonProps }
 */
export const useProductAndEdition = (): ProductAndEditionInfo => {
	const appEditions = useAppEditions();
	const { loading, data: project } = useProject();
	const siteHasOnlyJira = useSiteHasOnlyJira();

	// Show premium pill button for JSM premium trial experiment
	const projectType = useMemo(() => {
		if (loading) return;

		const type = toProjectType(project?.type);

		if (type) return type;

		if (siteHasOnlyJira) return SOFTWARE_PROJECT;
	}, [loading, project?.type, siteHasOnlyJira]);

	const applicationEdition = useMemo(() => {
		if (!projectType) return;

		if (siteHasOnlyJira) return getEditionForProject(SOFTWARE_PROJECT, appEditions);

		return getEditionForProject(projectType, appEditions);
	}, [appEditions, projectType, siteHasOnlyJira]);

	const { product, edition } = useMemo(() => {
		if (!projectType || !applicationEdition) {
			// We don't have a product or edition, so return nothing
			return {};
		}

		if (projectType === CORE_PROJECT && !siteHasOnlyJira) {
			// We are supporting only JWM implicit licensing when the parent license is JSW
			// Do not show on core products if site does not have only Jira
			return {};
		}
		return {
			product: PRODUCT_MAP[projectType],
			edition: EDITION_MAP[applicationEdition],
		};
	}, [applicationEdition, projectType, siteHasOnlyJira]);

	return {
		product,
		edition,
	};
};
